import { useEffect, useState } from 'react'
import { Activity, BarChart3, Globe2, HardDrive, Mail, MailWarning, PieChart, ShieldCheck, TrendingUp, Users } from 'lucide-react'
import { api } from '../../api/client'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

type DashboardData = {
  activeDomains: number
  mailboxUsers: number
  queuedMessages: number
  spamRate: number
  todayMessages: number
  storageUsedMb: number
  storageTotalMb: number
  recentUsers: { id: string; email: string; createdAt: string }[]
  dailyMessages?: number[]
  dailySpam?: number[]
  mailBreakdown?: { inbox: number; sent: number; spam: number; draft: number }
}

function MiniLineChart({ data, color = 'hsl(var(--primary))', height = 48, className = '' }: { data: number[]; color?: string; height?: number; className?: string }) {
  if (!data.length) return null
  const max = Math.max(...data, 1)
  const min = Math.min(...data, 0)
  const range = max - min || 1
  const w = 200
  const points = data.map((v, i) => `${(i / (data.length - 1)) * w},${height - ((v - min) / range) * (height - 6) - 3}`).join(' ')
  const areaPoints = `0,${height} ${points} ${w},${height}`
  const gradientId = `area-${color.replace(/[^a-z0-9]/g, '')}`
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className={`w-full ${className}`} preserveAspectRatio="none" style={{ height }}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.15" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#${gradientId})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      {data.length > 0 && (() => {
        const lastX = w
        const lastY = height - ((data[data.length - 1] - min) / range) * (height - 6) - 3
        return <circle cx={lastX} cy={lastY} r="3.5" fill={color} stroke="white" strokeWidth="2" />
      })()}
    </svg>
  )
}

function BarChart({ data, labels, color = 'hsl(var(--primary))', height = 160 }: { data: number[]; labels: string[]; color?: string; height?: number }) {
  const max = Math.max(...data, 1)
  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {data.map((v, i) => (
        <div key={i} className="group flex flex-1 flex-col items-center gap-1 relative">
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-foreground text-background text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none z-10">
            {v}
          </div>
          <div className="w-full rounded-md transition-all duration-500 hover:opacity-100 cursor-default"
            style={{ height: `${Math.max((v / max) * 100, 2)}%`, background: `linear-gradient(to top, ${color}, ${color}dd)`, opacity: 0.75 + (v / max) * 0.25 }} />
          <span className="text-[9px] text-muted-foreground leading-none mt-1">{labels[i]}</span>
        </div>
      ))}
    </div>
  )
}

function DonutChart({ segments, size = 140, strokeWidth = 20 }: { segments: { value: number; color: string; label: string }[]; size?: number; strokeWidth?: number }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  let offset = 0
  const gap = 3

  return (
    <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center sm:gap-6">
      <div className="relative shrink-0">
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth={strokeWidth} className="opacity-30" />
          {segments.map((seg, i) => {
            const segLength = (seg.value / total) * circumference
            const dash = Math.max(segLength - gap, 0)
            const currentOffset = offset
            offset += segLength
            if (seg.value === 0) return null
            return (
              <circle key={i} cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={seg.color} strokeWidth={strokeWidth}
                strokeDasharray={`${dash} ${circumference - dash}`} strokeDashoffset={-currentOffset - gap / 2} strokeLinecap="round" className="transition-all duration-700 drop-shadow-sm" />
            )
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold">{total}</span>
          <span className="text-[10px] text-muted-foreground">total</span>
        </div>
      </div>
      <div className="flex flex-col gap-2.5">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center gap-2.5 text-sm">
            <span className="h-3 w-3 rounded-sm shrink-0 shadow-sm" style={{ backgroundColor: seg.color }} />
            <span className="text-muted-foreground">{seg.label}</span>
            <span className="font-semibold ml-auto tabular-nums">{seg.value}</span>
            <span className="text-[10px] text-muted-foreground w-8 text-right">{Math.round((seg.value / total) * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function ProgressRing({ value, max, size = 80, strokeWidth = 8, color = 'hsl(var(--primary))' }: { value: number; max: number; size?: number; strokeWidth?: number; color?: string }) {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const pct = Math.min(value / (max || 1), 1)
  const dash = pct * circumference

  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth={strokeWidth} className="opacity-30" />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={`${dash} ${circumference - dash}`} strokeLinecap="round" className="transition-all duration-1000 drop-shadow-sm" />
    </svg>
  )
}

function EmptyChart({ icon: Icon, label }: { icon: typeof Mail; label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-muted-foreground/50">
      <Icon size={32} strokeWidth={1.5} />
      <p className="mt-2 text-sm">{label}</p>
    </div>
  )
}

export function AdminDashboardPage() {
  const { t } = useI18n()
  const [stats, setStats] = useState<DashboardData | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get<DashboardData>('/admin/dashboard').then(setStats).catch((e: Error) => setError(e.message))
  }, [])

  const weekLabels = (() => {
    const days = []
    const count = stats?.dailyMessages?.length ?? 14
    for (let i = count - 1; i >= 0; i--) {
      const d = new Date()
      d.setDate(d.getDate() - i)
      days.push(d.toLocaleDateString(undefined, { weekday: 'short' }).slice(0, 2))
    }
    return days
  })()

  const cards = [
    { label: 'admin.activeDomains', icon: Globe2, value: stats?.activeDomains, color: 'text-blue-600', bg: 'bg-gradient-to-br from-blue-50 to-blue-100/50', iconBg: 'bg-blue-100' },
    { label: 'admin.mailboxUsers', icon: Users, value: stats?.mailboxUsers, color: 'text-emerald-600', bg: 'bg-gradient-to-br from-emerald-50 to-emerald-100/50', iconBg: 'bg-emerald-100' },
    { label: 'admin.queuedMessages', icon: MailWarning, value: stats?.queuedMessages, color: 'text-amber-600', bg: 'bg-gradient-to-br from-amber-50 to-amber-100/50', iconBg: 'bg-amber-100' },
    { label: 'admin.spamRate', icon: ShieldCheck, value: stats ? `${stats.spamRate}%` : undefined, color: 'text-red-600', bg: 'bg-gradient-to-br from-red-50 to-red-100/50', iconBg: 'bg-red-100' },
    { label: 'admin.todayMessages', icon: Mail, value: stats?.todayMessages, color: 'text-violet-600', bg: 'bg-gradient-to-br from-violet-50 to-violet-100/50', iconBg: 'bg-violet-100' },
    { label: 'admin.storageUsed', icon: HardDrive, value: stats?.storageUsedMb != null ? `${stats.storageUsedMb} MB` : undefined, color: 'text-cyan-600', bg: 'bg-gradient-to-br from-cyan-50 to-cyan-100/50', iconBg: 'bg-cyan-100' },
  ]

  return <>
    <PageHeader title="admin.dashboard" description="admin.overview" />
    {error && <p className="mb-5 rounded-lg bg-destructive/10 px-4 py-2.5 text-sm text-destructive">{error}</p>}

    {/* Stats cards */}
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
      {cards.map(({ label, icon: Icon, value, color, bg, iconBg }) => (
        <section key={label} className="panel p-5 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 group overflow-hidden relative">
          <div className={`absolute -top-6 -right-6 h-24 w-24 rounded-full ${iconBg} opacity-30 transition-transform duration-300 group-hover:scale-150`} />
          <div className="relative flex items-center justify-between">
            <p className="text-sm font-medium text-muted-foreground">{t(label)}</p>
            <span className={`rounded-xl p-2.5 ${bg} ${color} shadow-sm transition-transform duration-200 group-hover:scale-110`}><Icon size={18} /></span>
          </div>
          <p className="relative mt-3 text-3xl font-bold tracking-tight">{value ?? '—'}</p>
        </section>
      ))}
    </div>

    {/* Charts row */}
    <div className="mt-6 grid gap-6 grid-cols-1 lg:grid-cols-3">
      {/* Messages trend chart */}
      <section className="panel p-5 sm:p-6 lg:col-span-2 overflow-hidden">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold">
              <TrendingUp size={18} className="text-primary" />
              {t('admin.messagesTrend')}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">{t('admin.last14Days')}</p>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-primary shadow-sm" />{t('admin.messages')}</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-400 shadow-sm" />{t('admin.spam')}</span>
          </div>
        </div>
        {stats?.dailyMessages ? (
          <>
            <BarChart data={stats.dailyMessages} labels={weekLabels} color="hsl(var(--primary))" height={150} />
            {stats.dailySpam && (
              <div className="mt-4 pt-3 border-t border-dashed">
                <p className="text-[10px] text-muted-foreground mb-1.5 uppercase tracking-wider font-medium">{t('admin.spam')}</p>
                <MiniLineChart data={stats.dailySpam} color="#f87171" height={36} />
              </div>
            )}
          </>
        ) : (
          <EmptyChart icon={BarChart3} label={t('admin.noChartData')} />
        )}
      </section>

      {/* Mail breakdown donut */}
      <section className="panel p-5 sm:p-6">
        <h2 className="flex items-center gap-2 text-base font-semibold mb-5">
          <PieChart size={18} className="text-primary" />
          {t('admin.mailBreakdown')}
        </h2>
        {stats?.mailBreakdown ? (
          <DonutChart segments={[
            { value: stats.mailBreakdown.inbox, color: '#3b82f6', label: t('admin.inbox') },
            { value: stats.mailBreakdown.sent, color: '#10b981', label: t('admin.sent') },
            { value: stats.mailBreakdown.spam, color: '#ef4444', label: t('admin.spamLabel') },
            { value: stats.mailBreakdown.draft, color: '#8b5cf6', label: t('admin.draft') },
          ]} />
        ) : (
          <EmptyChart icon={PieChart} label={t('admin.noChartData')} />
        )}
      </section>
    </div>

    {/* Bottom row: storage + recent users */}
    <div className="mt-6 grid gap-6 grid-cols-1 lg:grid-cols-2">
      {/* Storage gauge */}
      <section className="panel p-5 sm:p-6">
        <h2 className="flex items-center gap-2 text-base font-semibold mb-5">
          <HardDrive size={18} className="text-primary" />
          {t('admin.storageOverview')}
        </h2>
        {stats?.storageUsedMb != null && stats.storageTotalMb > 0 ? (
          <div className="flex items-center gap-6">
            <div className="relative shrink-0">
              <ProgressRing value={stats.storageUsedMb} max={stats.storageTotalMb} size={88} strokeWidth={10} color="hsl(var(--primary))" />
              <span className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-bold">{Math.round((stats.storageUsedMb / stats.storageTotalMb) * 100)}%</span>
                <span className="text-[9px] text-muted-foreground">{t('admin.used')}</span>
              </span>
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-muted-foreground">{t('admin.used')}</span>
                  <span className="font-semibold">{stats.storageUsedMb} MB</span>
                </div>
                <div className="h-2.5 rounded-full bg-muted/50 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-primary/80 to-primary transition-all duration-1000 shadow-sm" style={{ width: `${(stats.storageUsedMb / stats.storageTotalMb) * 100}%` }} />
                </div>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground pt-1">
                <span>{t('admin.totalStorage')}: {stats.storageTotalMb >= 1024 ? `${(stats.storageTotalMb / 1024).toFixed(1)} GB` : `${stats.storageTotalMb} MB`}</span>
                <span>{t('admin.free')}: {stats.storageTotalMb - stats.storageUsedMb} MB</span>
              </div>
            </div>
          </div>
        ) : (
          <EmptyChart icon={HardDrive} label={t('admin.noChartData')} />
        )}
      </section>

      {/* Recent users */}
      <section className="panel p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity size={18} className="text-primary" />
          <h2 className="text-base font-semibold">{t('admin.recentUsers')}</h2>
        </div>
        {stats?.recentUsers && stats.recentUsers.length > 0 ? (
          <div className="space-y-1 max-h-[220px] overflow-y-auto scrollbar-thin">
            {stats.recentUsers.map((user, i) => (
              <div key={user.id} className="flex items-center justify-between py-2.5 px-3 text-sm rounded-lg hover:bg-muted/40 transition-colors group/row"
                style={{ animationDelay: `${i * 50}ms` }}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/5 text-primary text-xs font-bold shrink-0 shadow-sm">
                    {user.email.charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <span className="font-medium truncate block text-sm">{user.email}</span>
                    <span className="text-[10px] text-muted-foreground">{new Date(user.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyChart icon={Users} label={t('admin.emptyUsers')} />
        )}
      </section>
    </div>
  </>
}
